import React, { useId } from 'react';
import type { Tone } from '../../domain/types';
import { checkboxToneColor } from './checkboxTone';
import { CheckboxCopy, CheckboxMark } from './CheckboxParts';

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  indeterminate?: boolean;
  tone?: Tone;
  size?: number | string;
  id?: string;
  name?: string;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
  'aria-label'?: string;
}

const NATIVE_INPUT_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  indeterminate = false,
  tone = 'accent',
  size = 18,
  id,
  name,
  className = '',
  style,
  ariaLabel,
  'aria-label': ariaLabelAttr,
}) => {
  const generatedId = useId();
  const inputId = id || `z-checkbox-${generatedId}`;
  const descId = description ? `z-checkbox-desc-${generatedId}` : undefined;
  const activeColor = checkboxToneColor(tone);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key !== ' ' && e.key !== 'Enter') return;
    e.preventDefault();
    onChange(!checked);
  };

  return (
    <label
      htmlFor={inputId}
      className={`z-checkbox-container ${disabled ? 'z-checkbox-disabled' : ''} ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'flex-start',
        gap: '10px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        userSelect: 'none',
        ...style,
      }}
    >
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginTop: description ? '2px' : '0' }}>
        <input
          id={inputId}
          name={name}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          onKeyDown={handleKeyDown}
          aria-checked={indeterminate ? 'mixed' : checked}
          aria-label={ariaLabel || ariaLabelAttr}
          aria-describedby={descId}
          className="sr-only"
          style={NATIVE_INPUT_STYLE}
        />
        <CheckboxMark checked={checked} indeterminate={indeterminate} activeColor={activeColor} size={size} />
      </div>
      <CheckboxCopy label={label} description={description} descId={descId} />
    </label>
  );
};
